import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { ContactDto } from '../models/contact.dto';
import { EmailMessageDto } from '../models/email-message.dto';
import { MailService } from '../services/mail.service';
import { ToastService } from '../toast/toast.service';
import { Utils } from '../utils';

@Component({
  selector: 'app-email-composer',
  templateUrl: './email-composer.component.html',
  standalone: false,
})
export class EmailComposerComponent implements OnInit {
  public form: UntypedFormGroup;
  public submitted: boolean = false;

  public contacts: ContactDto[] = [];

  private beneficiaryId: number;
  private beneficiaryName: string;
  protected to: string | ContactDto;
  private content: string = null;

  // Attachments
  public attachmentsForm: UntypedFormGroup;
  public maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2;
  public maxFileSizeStr: string = Utils.humanFileSize(this.maxFileSize, true, 2);

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly mailService: MailService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.contacts = await this.mailService.getContacts();
    this.loadForm();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      from: [{ name: 'Insertis', email: 'noreply@grandlyon.com' } as ContactDto, Validators.required, false],
      to: [this.to, Validators.required],
      cc: [],
      subject: [],
      content: [this.content, Validators.required],
    });
    this.form.controls.from.disable();
    this.form.controls.to.disable();

    this.initAttachementForm();
  }

  public onInit(beneficiaryId: number, beneficiaryName: string, to: string | ContactDto, content: string = null): void {
    this.beneficiaryId = beneficiaryId;
    this.beneficiaryName = beneficiaryName;
    this.to = to;
    this.content = content;
  }
  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.form.valid && this.attachmentsForm.valid) {
      const message: EmailMessageDto = {
        beneficiaryId: this.beneficiaryId,
        beneficiaryName: this.beneficiaryName,
        to: [this.form.controls.to.value],
        cc: this.form.controls.cc.value,
        subject: this.form.controls.subject.value,
        content: this.form.controls.content.value,
        attachments: (this.attachments.value ?? []).filter((value) => value.document !== null),
      } as EmailMessageDto;

      if (Utils.isNotEmpty(message.subject) || (!Utils.isNotEmpty(message.subject) && (await this.openConfirmationModal()))) {
        await this.mailService.sendMail(message);
        this.toastService.show('Votre message électronique a été envoyé avec succès.', 'success');
        this.cancel();
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  public compareContacts: (item: ContactDto, selected: ContactDto) => boolean = (item: ContactDto, selected: ContactDto): boolean =>
    item.email === selected.email;

  public searchContacts: (term: string, item: ContactDto) => boolean = (term: string, item: ContactDto): boolean => {
    term = term.toLowerCase();
    return item.name.toLowerCase().indexOf(term) > -1 || item.email.toLowerCase().indexOf(term) > -1;
  };

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Voulez-vous envoyer ce message sans objet ?');
  }

  // *********************************************************************************************
  // * ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT A *
  // * ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT ATTACHMENT A *
  // *********************************************************************************************

  private initAttachementForm(): void {
    this.attachmentsForm = this.formBuilder.group({
      attachments: this.formBuilder.array([]),
    });
    this.addAttachment();
  }

  public get attachments(): UntypedFormArray {
    return this.attachmentsForm.controls.attachments as UntypedFormArray;
  }

  public addAttachment(): void {
    this.attachments.push(this.formBuilder.group({ document: [] }));
  }

  public removeAttachment(index: number): void {
    if (this.attachments.length === 1) {
      this.attachments.at(0).setValue({ document: null });
    } else {
      this.attachments.removeAt(index);
    }
  }
}
